<script setup lang="ts">
import { computed, ComputedRef } from "vue";
import useVuelidate from "@vuelidate/core";

import { useQuoteStore } from "../stores/quotes";
import { useApplicationStore } from "../stores/application";
import { formatCurrency } from "../composables/formatCurrency";
import Modal from "./Modal.vue";
import ApplicationAddPensionFund from "./ApplicationAddPensionFund.vue";
import { TaxFreeType } from "../stores/pensionOptions";
import { getTaxFreeCash } from "@/utils";

type FundValidator = {
    fundsToAdd: ComputedRef<number>,
    taxFreeCashToAdd: ComputedRef<number>,
    numberOfAddedFunds: ComputedRef<number>,
    taxFreeAmount: ComputedRef<number>,
    totalTaxFreeCash: ComputedRef<number>,
}

const application = useApplicationStore();
const quote = useQuoteStore();

const totalFund = computed(() => quote.quote?.product.totalFund ?? 0);

const taxFreeAmount = computed(() => {
    if (!quote.quote) {
        return 0;
    }

    if (quote.quote.product.taxFree.type === TaxFreeType.Full) {
        return totalFund.value * (quote.quote.product.taxFree.fraction / 100);
    }

    return quote.quote.product.taxFree.value;
});

const fundsAdded =
    computed(() => application.pensionFunds.reduce((pre, cur) => pre + cur.totalFund, 0));

const fundsToAdd = computed(() => totalFund.value - fundsAdded.value);

const errorMessages = computed(() => ({
    taxFreeCashToAdd: `The total tax free cash does not match the tax free cash used for your quote.
            You previously added ${formatCurrency(taxFreeAmount.value ?? 0, 2)}`,
    fundsToAdd: `The pension funds added do not match the total funds used for your quote. You previously added ${formatCurrency(totalFund.value ?? 0, 2)}`,
    numberOfAddedFunds: "You have to add at least one fund",
    numberOfAddedFundsCL: "You can only have one fund for a Canada Life application",
}));

const removePot = (index: number) => application.pensionFunds.splice(index, 1);

const totalTaxFreeCash = computed(() =>
    application.pensionFunds.reduce((pre, cur) => pre + getTaxFreeCash(cur), 0));

const taxFreeCashToAdd = computed(() => taxFreeAmount.value - totalTaxFreeCash.value);

const numberOfAddedFunds = computed(() => application.pensionFunds.length);

// we can only allow canada life customers to do one fund - so we hide the button for them when 1 fund is added
const showAddFundBtn = computed(() =>
    (application.companySlug === "cl" && numberOfAddedFunds.value === 0) || application.companySlug !== "cl");

const rules = computed(() => {
    return {
        funds: {
            fundsToAdd: (value: FundValidator) =>
                value.fundsToAdd.value === 0 || value.numberOfAddedFunds.value === 0,
            taxFreeCashToAdd: (value: FundValidator) =>
                value.taxFreeCashToAdd.value === 0 ||
                value.numberOfAddedFunds.value === 0 ||
                (value.totalTaxFreeCash.value < value.taxFreeAmount.value && value.fundsToAdd.value !== 0),
            numberOfAddedFunds: (value: FundValidator) => value.numberOfAddedFunds.value > 0,
            numberOfAddedFundsCL: (value: FundValidator) =>
                application.companySlug === "cl" ? value.numberOfAddedFunds.value <= 1 : true,
        },
    };
});

const v$ = useVuelidate(rules,
    { funds: { fundsToAdd, taxFreeCashToAdd, numberOfAddedFunds, taxFreeAmount, totalTaxFreeCash } });

const handleOnClickOpenModal = (index: number | null) => {
    application.pensionFundsIndex = index;
    application.pensionFundsModal = true;
};

</script>
<template>
    <div class="row">
        <div class="col-12 mt-1">
            <h4 class="line-height-intro mt-3">
                Your pension fund
            </h4>
            <p class="alert alert-info">
                Enter details for each of your pension fund sources below.
                Please add, edit or remove fund sources until you have reached your required total fund value.
                This should match the amount you entered to get your quote.
            </p>
            <p>
                Your quote is based on
            </p>
            <ul>
                <li class="p-2">
                    A total pension fund of {{ formatCurrency(totalFund ?? 0, 2) }}.
                </li>
                <li class="p-2">
                    Total tax-free lump sum from your pension fund is {{ formatCurrency(taxFreeAmount ?? 0, 2) }}.
                </li>
            </ul>
            <p>
                We now need details of your current pension fund provider(s). Click ‘Add fund’ below to begin.
            </p>
            <p class="font-weight-bold-col mt-2">
                Fund details
            </p>
            <div
                v-if="fundsAdded > 0"
                id="funds-table"
            >
                <div class="row text-secondary col-12 mx-0 px-0 font-weight-bold text-center">
                    <span
                        class="col-3 col-lg-3 bg-light text-info border table-text-center-3 align-items-center-2 py-3"
                    >Pension fund provider</span> <span
                        class="col-3 col-lg-2 bg-light text-info border table-text-center-3 margin-table align-items-center-2 py-3"
                    >Pension fund value</span> <span
                        class="col-3 col-lg-2 bg-light text-info border table-text-center-3 margin-table align-items-center-2 py-3"
                    >Tax-free lump sum</span> <span
                        class="col-3 col-lg-2 bg-light text-info border table-text-center-3 margin-table align-items-center-2 py-3"
                    >Funds used to purchase the annuity (after tax-free lump sum)</span>
                </div>
                <div
                    v-for="(fund, index) in application.pensionFunds"
                    :key="index"
                    class="row text-center col-12 mx-0 px-0"
                >
                    <span
                        :data-testid="'provider_' + index"
                        class="col-3 col-lg-3 border table-text-center-3 align-items-center-2 margin-table-top"
                    >
                        {{ fund.provider }}</span>
                    <span
                        :data-testid="'fund_' + index"
                        class="col-3 col-lg-2 border table-text-center-3 margin-table margin-table-top align-items-center-2"
                    >{{
                        formatCurrency(fund.totalFund, 2) }}</span>
                    <span
                        :data-testid="'taxfree_' + index"
                        class="col-3 col-lg-2 border table-text-center-3 margin-table margin-table-top align-items-center-2"
                    >{{
                        formatCurrency(getTaxFreeCash(fund), 2) }}</span>
                    <span
                        :data-testid="'purchase_' + index"
                        class="col-3 col-lg-2 border table-text-center-3 margin-table margin-table-top align-items-center-2"
                    >{{
                        formatCurrency(fund.totalFund - getTaxFreeCash(fund)) }}</span>
                    <div class="col-12 col-lg-3 border table-text-center-2 margin-table margin-table-top">
                        <button
                            :id="'edit_fund_' + index"
                            type="button"
                            class="btn col-5 col-lg-8 btn-primary mb-3 me-1 float-left font-weight-bold mt-3 table-text-center-2"
                            @click="handleOnClickOpenModal(index)"
                        >
                            Edit
                        </button> <button
                            :id="'remove_fund_' + index"
                            type="button"
                            class="btn btn-danger mb-3 float-left col-5 col-lg-8 ms-2 ms-lg-0 font-weight-bold table-text-center-2 mt-3 mt-lg-0"
                            @click="removePot(index)"
                        >
                            Remove
                        </button>
                    </div>
                </div>
                <div class="row text-secondary col-12 mx-0 px-0 font-weight-bold text-center">
                    <span
                        class="col-3 col-lg-3 bg-light text-info border
                            table-text-center-3 pt-3 margin-table-top pb-3 align-items-center-2"
                    >Total</span>
                    <span
                        data-testid="total_fund"
                        class="col-3 col-lg-2 bg-light text-info border
                            table-text-center-3 margin-table margin-table-top pt-3 pb-3 align-items-center-2"
                    >{{
                        formatCurrency(fundsAdded, 2) }}</span>
                    <span
                        data-testid="total_taxfree"
                        class="col-3 col-lg-2 bg-light text-info border
                            table-text-center-3 margin-table margin-table-top pt-3 pb-3 align-items-center-2"
                    >{{
                        formatCurrency(totalTaxFreeCash, 2) }}</span>
                    <span
                        data-testid="total_purchase"
                        class="col-3 col-lg-2 bg-light text-info border
                            table-text-center-3 margin-table margin-table-top pt-3 pb-3 align-items-center-2"
                    >{{
                        formatCurrency(fundsAdded - totalTaxFreeCash, 2) }}</span>
                </div>
            </div>
            <div
                v-if="fundsAdded === 0 || (v$.$error && fundsToAdd >= 0)"
                id="funds-summary"
                class="col-md-10 chalet red mt-2"
            >
                <p>
                    You told us your pension fund is {{ formatCurrency(totalFund ?? 0, 2) }}<br><br>
                    The pensions funds you have added total {{ formatCurrency(fundsAdded, 2) }}<br><br>
                    You need to add an additional {{ formatCurrency(fundsToAdd, 2) }} of pension funds to continue<br>
                </p>
            </div>
            <div
                v-if="application.companySlug === 'cl'"
                class="row mt-3 col-12"
            >
                <span>Canada Life allow a maximum of 1 fund per application.</span>
            </div>
        </div>
    </div>

    <div class="d-flex">
        <button
            v-if="showAddFundBtn"
            id="add_fund"
            type="button"
            class="btn btn-primary my-3 float-left font-weight-bold"
            @click="handleOnClickOpenModal(null)"
        >
            Add fund
        </button>
    </div>

    <Modal
        id="fund-details-modal"
        header="Fund details"
        close-focus="add_fund"
        :cross="false"
        :show="application.pensionFundsModal"
        @close="application.pensionFundsModal = false"
    >
        <template #body>
            <ApplicationAddPensionFund @close="application.pensionFundsModal = false" />
        </template>
    </Modal>
    <div
        v-if="v$.$error"
        class="field-validation-error"
    >
        <div
            v-for="error of v$.$errors"
            :key="error.$uid"
            class="alert alert-danger"
        >
            {{ errorMessages[error.$validator as keyof typeof errorMessages] }}
        </div>
    </div>
</template>
